// src/features/system/admin/components/UserManagementV2.tsx
//
// Real IAM User Management panel.
// Calls the Go orchestrator via BFF /orchestrator/iam/* endpoints.
// Hybrid architecture: dedicated iam_users table for auth fields,
// scope linkage (iam.user namespace) for identity/metadata/audit.
// Uses id_user (auto-incremented, no external FK) as the user identifier.

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import MinimalisticTable, { ColumnDef } from '@/shared/components/ui/MinimalisticTable';
import AppDialog from '@/shared/components/ui/AppDialog';
import { useDocumentTitle } from '@/hooks/useDocumentTitle';
import type { IAMUser } from '@/features/system/iam/api/iamApi';
import {
  useIAMUsers,
  useCreateIAMUser,
  useUpdateIAMProfile,
  useChangeIAMPassword,
  useDeactivateIAMUser,
  useActivateIAMUser,
  useUpdateIAMRoles,
  useDeleteIAMUser,
} from '@/features/system/iam/hooks/useIAMUsers';
import { useAuth } from '@/shared/auth/session';
import {
  useSendWelcome,
  useSendReset,
  useSendNotify,
} from '@/features/system/iam/hooks/useComms';
import { useTenants } from '@/features/system/tenants/hooks/useTenants';

/* ─── Constants ───────────────────────────────────────────── */

const STATUS_CLS: Record<string, string> = {
  active:   'bg-green-900/30 text-green-400',
  inactive: 'bg-neutral-700/40 text-neutral-400',
  locked:   'bg-red-900/30 text-red-400',
};

const KNOWN_ROLES = ['superadmin', 'admin', 'operator', 'guest', 'service'] as const;

/* ─── Sub-dialogs ─────────────────────────────────────────── */

function CreateUserDialog({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
}) {
  const create   = useCreateIAMUser();
  const { data: tenants = [] } = useTenants();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [fullName, setFullName] = useState('');
  const [email,    setEmail]    = useState('');
  const [role,         setRole]         = useState<string>('');
  const [tenantId,     setTenantId]     = useState<string>('');
  const [sendWelcome,  setSendWelcome]  = useState(false);
  const [language,     setLanguage]     = useState('en');
  const [serverError,  setServerError]  = useState<string | null>(null);

  const reset = () => {
    setUsername(''); setPassword(''); setFullName(''); setEmail('');
    setRole(''); setTenantId(''); setSendWelcome(false); setLanguage('en'); setServerError(null);
  };

  /* ── Password strength rules (mirrored from Go ValidatePassword) ── */
  const pwRules = useMemo(() => [
    { key: 'len',   label: '8+ characters',        ok: password.length >= 8 },
    { key: 'upper', label: 'Uppercase letter (A-Z)', ok: /[A-Z]/.test(password) },
    { key: 'lower', label: 'Lowercase letter (a-z)', ok: /[a-z]/.test(password) },
    { key: 'digit', label: 'Digit (0-9)',            ok: /\d/.test(password) },
  ], [password]);
  const passwordValid = pwRules.every(r => r.ok);
  const passwordDirty = password.length > 0;

  const canSubmit = username.trim() && passwordValid && role !== '' && tenantId !== '' && !create.isPending;

  // Auto-enable welcome when email is entered, auto-disable when cleared.
  const handleEmailChange = (v: string) => {
    setEmail(v);
    setSendWelcome(v.trim().length > 0);
  };

  const onConfirm = async () => {
    if (!canSubmit) return;
    setServerError(null);
    try {
      await create.mutateAsync({
        username: username.trim(),
        password,
        full_name: fullName.trim() || undefined,
        email: email.trim() || undefined,
        roles: [role],
        tenant_id: Number(tenantId),
        send_welcome: sendWelcome && email.trim().length > 0,
        preferences: { language },
      });
      reset();
      onOpenChange(false);
    } catch (err: any) {
      setServerError(err?.message ?? 'Could not create user.');
    }
  };

  return (
    <AppDialog
      open={open}
      onOpenChange={v => { if (!v) reset(); onOpenChange(v); }}
      title="Create IAM User"
      size="md"
      confirmLabel={create.isPending ? 'Creating…' : 'Create user'}
      confirmDisabled={!canSubmit}
      isBusy={create.isPending}
      lockOnBusy
      onConfirm={onConfirm}
    >
      <label className="block text-sm text-neutral-300">
        Username *
        <input autoFocus value={username} onChange={e => setUsername(e.target.value)}
          placeholder="jdoe"
          className="mt-1 w-full bg-black border border-neutral-700 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-emerald-500" />
      </label>

      <div className="space-y-1">
        <label className="block text-sm text-neutral-300">
          Password *
          <input type="password" value={password} onChange={e => { setPassword(e.target.value); setServerError(null); }}
            placeholder="••••••••"
            className={`mt-1 w-full bg-black border border-neutral-700 rounded px-3 py-2 focus:outline-none focus:ring-2 ${
              passwordDirty && !passwordValid ? 'ring-2 ring-amber-500/60 focus:ring-amber-500' : 'focus:ring-emerald-500'
            }`} />
        </label>
        {/* Live password-strength checklist */}
        {passwordDirty && (
          <div className="grid grid-cols-2 gap-x-3 gap-y-0.5 mt-1">
            {pwRules.map(r => (
              <span key={r.key} className={`text-xs flex items-center gap-1.5 transition-colors ${
                r.ok ? 'text-emerald-400' : 'text-neutral-500'
              }`}>
                <span className={`inline-block w-3.5 h-3.5 rounded-full text-[10px] leading-[14px] text-center font-bold ${
                  r.ok ? 'bg-emerald-500/20 text-emerald-400' : 'bg-neutral-700 text-neutral-500'
                }`}>{r.ok ? '✓' : '·'}</span>
                {r.label}
              </span>
            ))}
          </div>
        )}
        {!passwordDirty && (
          <span className="text-xs text-neutral-500 block">Must include uppercase, lowercase, digit, and 8+ chars</span>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <label className="block text-sm text-neutral-300">
          Full Name
          <input value={fullName} onChange={e => setFullName(e.target.value)}
            placeholder="John Doe"
            className="mt-1 w-full bg-black border border-neutral-700 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-emerald-500" />
        </label>

        <label className="block text-sm text-neutral-300">
          Email
          <input type="email" value={email} onChange={e => handleEmailChange(e.target.value)}
            placeholder="jdoe@example.com"
            className="mt-1 w-full bg-black border border-neutral-700 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-emerald-500" />
        </label>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <label className="block text-sm text-neutral-300">
          Tenant *
          <select value={tenantId} onChange={e => setTenantId(e.target.value)}
            className="mt-1 w-full bg-black border border-neutral-700 rounded px-3 py-2">
            <option value="">— select tenant —</option>
            {tenants.map(t => (
              <option key={t.id_tenant} value={String(t.id_tenant)}>
                {t.display_name ?? t.code}
              </option>
            ))}
          </select>
        </label>

        <label className="block text-sm text-neutral-300">
          Role *
          <select value={role} onChange={e => setRole(e.target.value)}
            className="mt-1 w-full bg-black border border-neutral-700 rounded px-3 py-2">
            <option value="">— select role —</option>
            {KNOWN_ROLES.map(r => <option key={r} value={r}>{r}</option>)}
          </select>
        </label>
      </div>

      {/* Preferences */}
      <div className="border border-neutral-800 rounded-lg p-3 space-y-2">
        <p className="text-xs font-medium text-neutral-500 uppercase tracking-wider">Preferences</p>
        <label className="block text-sm text-neutral-300">
          Language
          <select value={language} onChange={e => setLanguage(e.target.value)}
            className="mt-1 w-full bg-black border border-neutral-700 rounded px-3 py-2">
            <option value="en">English</option>
            <option value="es">Español</option>
          </select>
        </label>
      </div>

      {/* Welcome email toggle — only visible when email is provided */}
      {email.trim().length > 0 && (
        <label className="flex items-center gap-2 text-sm text-neutral-300 cursor-pointer select-none">
          <input
            type="checkbox"
            checked={sendWelcome}
            onChange={e => setSendWelcome(e.target.checked)}
            className="w-4 h-4 accent-emerald-500"
          />
          Send welcome email to <span className="text-emerald-400">{email.trim()}</span>
        </label>
      )}

      {/* Server error banner — stays visible inside the dialog */}
      {serverError && (
        <div className="flex items-start gap-2 bg-rose-950/60 border border-rose-800/60 rounded-lg px-3 py-2.5 animate-in fade-in slide-in-from-top-1">
          <span className="text-rose-400 text-sm mt-px">✕</span>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-rose-300">Creation failed</p>
            <p className="text-xs text-rose-400 mt-0.5 break-words">{serverError}</p>
          </div>
          <button onClick={() => setServerError(null)} className="text-rose-500 hover:text-rose-300 text-xs mt-0.5">dismiss</button>
        </div>
      )}
    </AppDialog>
  );
}

/* ─── Edit profile dialog ─────────────────────────────────── */

function EditProfileDialog({
  user, open, onOpenChange,
}: {
  user: IAMUser; open: boolean; onOpenChange: (v: boolean) => void;
}) {
  const update = useUpdateIAMProfile();
  const { user: authUser, refresh } = useAuth();
  const [fullName, setFullName] = useState(user.full_name);
  const [email,    setEmail]    = useState(user.email);
  const [language, setLanguage] = useState(user.preferences?.language ?? 'en');

  const onConfirm = async () => {
    await update.mutateAsync({
      idUser: user.id_user,
      full_name: fullName.trim() || undefined,
      email: email.trim() || undefined,
      preferences: { language },
    });
    // When editing the current principal, force a session refresh so locale
    // dependent UI (e.g. importer errors) reflects the new preference now.
    if (Number(authUser?.id_user) === Number(user.id_user)) {
      await refresh(true);
    }
    onOpenChange(false);
  };

  return (
    <AppDialog
      open={open}
      onOpenChange={onOpenChange}
      title={`Edit profile: ${user.username}`}
      size="sm"
      confirmLabel={update.isPending ? 'Saving…' : 'Save'}
      confirmDisabled={update.isPending}
      isBusy={update.isPending}
      lockOnBusy
      onConfirm={onConfirm}
    >
      <label className="block text-sm text-neutral-300">
        Full Name
        <input autoFocus value={fullName} onChange={e => setFullName(e.target.value)}
          className="mt-1 w-full bg-black border border-neutral-700 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-emerald-500" />
      </label>
      <label className="block text-sm text-neutral-300">
        Email
        <input type="email" value={email} onChange={e => setEmail(e.target.value)}
          className="mt-1 w-full bg-black border border-neutral-700 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-emerald-500" />
      </label>
      <div className="border border-neutral-800 rounded-lg p-3 space-y-2">
        <p className="text-xs font-medium text-neutral-500 uppercase tracking-wider">Preferences</p>
        <label className="block text-sm text-neutral-300">
          Language
          <select value={language} onChange={e => setLanguage(e.target.value)}
            className="mt-1 w-full bg-black border border-neutral-700 rounded px-3 py-2">
            <option value="en">English</option>
            <option value="es">Español</option>
          </select>
        </label>
      </div>
      {update.isError && (
        <p className="text-sm text-rose-400">{update.error?.message ?? 'Could not update profile.'}</p>
      )}
    </AppDialog>
  );
}

/* ─── Change password dialog ──────────────────────────────── */

function ChangePasswordDialog({
  user, open, onOpenChange,
}: {
  user: IAMUser; open: boolean; onOpenChange: (v: boolean) => void;
}) {
  const changePw = useChangeIAMPassword();
  const [newPw, setNewPw] = useState('');
  const [confirmPw, setConfirmPw] = useState('');

  const mismatch = confirmPw.length > 0 && newPw !== confirmPw;
  const canSubmit = newPw.length >= 6 && !mismatch && !changePw.isPending;

  const reset = () => { setNewPw(''); setConfirmPw(''); };

  const onConfirm = async () => {
    if (!canSubmit) return;
    await changePw.mutateAsync({ idUser: user.id_user, new_password: newPw });
    reset();
    onOpenChange(false);
  };

  return (
    <AppDialog
      open={open}
      onOpenChange={v => { if (!v) reset(); onOpenChange(v); }}
      title={`Reset password: ${user.username}`}
      size="sm"
      confirmLabel={changePw.isPending ? 'Changing…' : 'Change password'}
      confirmDisabled={!canSubmit}
      confirmTone="danger"
      isBusy={changePw.isPending}
      lockOnBusy
      onConfirm={onConfirm}
    >
      <label className="block text-sm text-neutral-300">
        New Password (min 6 chars)
        <input autoFocus type="password" value={newPw} onChange={e => setNewPw(e.target.value)}
          placeholder="••••••••"
          className="mt-1 w-full bg-black border border-neutral-700 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-emerald-500" />
      </label>
      <label className="block text-sm text-neutral-300">
        Confirm Password
        <input type="password" value={confirmPw} onChange={e => setConfirmPw(e.target.value)}
          placeholder="••••••••"
          className={`mt-1 w-full bg-black border border-neutral-700 rounded px-3 py-2 focus:outline-none focus:ring-2 ${mismatch ? 'ring-2 ring-rose-500' : 'focus:ring-emerald-500'}`} />
      </label>
      {mismatch && <p className="text-sm text-rose-400">Passwords do not match.</p>}
      {changePw.isError && (
        <p className="text-sm text-rose-400">{changePw.error?.message ?? 'Could not change password.'}</p>
      )}
    </AppDialog>
  );
}

/* ─── Update roles dialog ─────────────────────────────────── */

function UpdateRolesDialog({
  user, open, onOpenChange,
}: {
  user: IAMUser; open: boolean; onOpenChange: (v: boolean) => void;
}) {
  const updateRoles = useUpdateIAMRoles();
  const [selected, setSelected] = useState<Set<string>>(new Set(user.roles));

  const toggle = (r: string) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(r)) next.delete(r); else next.add(r);
      return next;
    });
  };

  const onConfirm = async () => {
    await updateRoles.mutateAsync({ idUser: user.id_user, roles: Array.from(selected) });
    onOpenChange(false);
  };

  return (
    <AppDialog
      open={open}
      onOpenChange={onOpenChange}
      title={`Roles: ${user.username}`}
      size="sm"
      confirmLabel={updateRoles.isPending ? 'Saving…' : 'Save roles'}
      confirmDisabled={selected.size === 0 || updateRoles.isPending}
      isBusy={updateRoles.isPending}
      lockOnBusy
      onConfirm={onConfirm}
    >
      <div className="flex flex-wrap gap-2">
        {KNOWN_ROLES.map(r => (
          <button key={r} onClick={() => toggle(r)}
            className={`px-3 py-1.5 rounded text-sm border transition-colors ${
              selected.has(r)
                ? 'bg-sky-900/50 border-sky-500 text-sky-300'
                : 'pac-glass-btn text-neutral-400 hover:border-neutral-500'
            }`}>
            {r}
          </button>
        ))}
      </div>
      {updateRoles.isError && (
        <p className="text-sm text-rose-400">{updateRoles.error?.message ?? 'Could not update roles.'}</p>
      )}
    </AppDialog>
  );
}

/* ─── Delete confirmation dialog ──────────────────────────── */

function DeleteUserDialog({
  user, open, onOpenChange,
}: {
  user: IAMUser; open: boolean; onOpenChange: (v: boolean) => void;
}) {
  const del = useDeleteIAMUser();
  const [typed, setTyped] = useState('');
  const confirmed = typed === user.username;

  const onConfirm = async () => {
    if (!confirmed) return;
    await del.mutateAsync(user.id_user);
    onOpenChange(false);
  };

  return (
    <AppDialog
      open={open}
      onOpenChange={v => { if (!v) setTyped(''); onOpenChange(v); }}
      title={`Delete user: ${user.username}`}
      description="This action cannot be undone. The user and all associated data will be permanently removed."
      size="sm"
      confirmLabel={del.isPending ? 'Deleting…' : 'Delete permanently'}
      confirmDisabled={!confirmed || del.isPending}
      confirmTone="danger"
      isBusy={del.isPending}
      lockOnBusy
      onConfirm={onConfirm}
    >
      <label className="block text-sm text-neutral-300">
        Type <code className="text-rose-400">{user.username}</code> to confirm
        <input autoFocus value={typed} onChange={e => setTyped(e.target.value)}
          placeholder={user.username}
          className="mt-1 w-full bg-black border border-neutral-700 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-rose-500" />
      </label>
      {del.isError && (
        <p className="text-sm text-rose-400">{del.error?.message ?? 'Could not delete user.'}</p>
      )}
    </AppDialog>
  );
}

/* ─── User detail panel (expanded row) ────────────────────── */

function UserDetailPanel({ user }: { user: IAMUser }) {
  const attrs = [
    { key: 'username',   value: user.username,   kind: 'string' },
    { key: 'full_name',  value: user.full_name,  kind: 'string' },
    { key: 'email',      value: user.email,      kind: 'string' },
    { key: 'status',     value: user.status,     kind: 'enum' },
    { key: 'roles',      value: JSON.stringify(user.roles), kind: 'jsonb' },
    { key: 'preferences',value: JSON.stringify(user.preferences ?? {}), kind: 'jsonb' },
    { key: 'tenant_id',  value: String(user.tenant_id),     kind: 'int' },
    { key: 'created_at', value: new Date(user.created_at).toLocaleString(), kind: 'timestamp' },
    { key: 'updated_at', value: new Date(user.updated_at).toLocaleString(), kind: 'timestamp' },
  ];

  return (
    <div className="pac-glass-subtle rounded p-4 space-y-3">
      <div className="flex items-center gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <span className="text-xs font-mono text-neutral-500">scope</span>
          <span className="bg-emerald-900/40 text-emerald-400 text-xs font-mono px-2 py-0.5 rounded">
            iam.user
          </span>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs font-mono text-neutral-500">id_user</span>
          <span className="text-neutral-300 text-xs font-mono">{user.id_user}</span>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs font-mono text-neutral-500">tenant</span>
          <span className="text-amber-400 text-xs font-mono">#{user.tenant_id}</span>
        </div>
      </div>

      <table className="w-full text-sm">
        <thead>
          <tr className="text-left text-neutral-500 text-xs">
            <th className="py-1 pr-4">field</th>
            <th className="py-1 pr-4">value</th>
            <th className="py-1">kind</th>
          </tr>
        </thead>
        <tbody>
          {attrs.map(a => (
            <tr key={a.key} className="border-t border-neutral-800/50">
              <td className="py-1.5 pr-4 font-mono text-xs text-neutral-400">{a.key}</td>
              <td className="py-1.5 pr-4 text-neutral-200">{a.value || '—'}</td>
              <td className="py-1.5 text-xs text-neutral-600">{a.kind}</td>
            </tr>
          ))}
        </tbody>
      </table>

      <LinkedProvidersSection idUser={user.id_user} />
    </div>
  );
}

/* ─── Federated identity providers (linked) ───────────────── */

function LinkedProvidersSection({ idUser }: { idUser: number }) {
  const [state, setState] = useState<{
    loading: boolean;
    error: string | null;
    providers: Array<{ name: string; email: string | null; linked_at: string | null }>;
    fetchedAt: number | null;
  }>({ loading: true, error: null, providers: [], fetchedAt: null });

  useEffect(() => {
    let cancelled = false;
    setState(s => ({ ...s, loading: true, error: null }));
    import('@/features/system/auth/api/authApi')
      .then(m => m.getAdminUserLinkedProviders(idUser))
      .then(result => {
        if (cancelled) return;
        if (!result) {
          setState({
            loading: false,
            error: 'Federation status unavailable (insufficient permission or upstream offline).',
            providers: [],
            fetchedAt: Date.now(),
          });
          return;
        }
        setState({
          loading: false,
          error: null,
          providers: result.providers,
          fetchedAt: result.fetched_at,
        });
      })
      .catch(err => {
        if (cancelled) return;
        setState({
          loading: false,
          error: String(err?.message || err || 'Failed to load linked providers.'),
          providers: [],
          fetchedAt: Date.now(),
        });
      });
    return () => { cancelled = true; };
  }, [idUser]);

  const stamp = state.fetchedAt ? new Date(state.fetchedAt).toLocaleTimeString() : '—';

  return (
    <div className="border-t border-neutral-800/50 pt-3">
      <div className="flex items-center justify-between gap-3 mb-2">
        <div className="flex items-center gap-2">
          <span className="text-xs font-mono text-neutral-500">linked_providers</span>
          <span className={`text-[10px] px-1.5 py-0.5 rounded border ${
            state.loading
              ? 'border-neutral-700 text-neutral-400'
              : state.providers.length > 0
                ? 'border-emerald-500/30 text-emerald-300'
                : 'border-amber-500/30 text-amber-200'
          }`}>
            {state.loading
              ? 'CHECKING'
              : state.providers.length > 0
                ? `${state.providers.length} LINKED`
                : 'NONE'}
          </span>
        </div>
        <span className="text-[10px] text-neutral-600 font-mono">checked {stamp}</span>
      </div>

      {state.error && (
        <p className="text-[11px] text-amber-300/80">{state.error}</p>
      )}

      {!state.loading && !state.error && state.providers.length === 0 && (
        <p className="text-[11px] text-neutral-500">
          No federated identity providers linked. This user authenticates with password only.
        </p>
      )}

      {state.providers.length > 0 && (
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-neutral-500 text-xs">
              <th className="py-1 pr-4">provider</th>
              <th className="py-1 pr-4">email_at_link</th>
              <th className="py-1">linked_at</th>
            </tr>
          </thead>
          <tbody>
            {state.providers.map(p => (
              <tr key={`${p.name}-${p.linked_at ?? ''}`} className="border-t border-neutral-800/50">
                <td className="py-1.5 pr-4 font-mono text-xs">
                  <span className="bg-emerald-900/30 text-emerald-300 px-1.5 py-0.5 rounded uppercase">
                    {p.name || 'unknown'}
                  </span>
                </td>
                <td className="py-1.5 pr-4 text-neutral-200">{p.email || '—'}</td>
                <td className="py-1.5 text-xs text-neutral-500">
                  {p.linked_at ? new Date(p.linked_at).toLocaleString() : '—'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

/* ─── Send notification dialog ────────────────────────────── */

function SendNotifyDialog({
  user, open, onOpenChange, onSent,
}: {
  user: IAMUser;
  open: boolean;
  onOpenChange: (v: boolean) => void;
  onSent: (msg: string) => void;
}) {
  const notify = useSendNotify();
  const [title,   setTitle]   = useState('');
  const [message, setMessage] = useState('');

  const reset = () => { setTitle(''); setMessage(''); };

  const canSubmit = title.trim().length > 0 && message.trim().length > 0 && !notify.isPending;

  const onConfirm = async () => {
    if (!canSubmit) return;
    await notify.mutateAsync({ idUser: user.id_user, title: title.trim(), message: message.trim() });
    onSent(`Notification sent to ${user.email}`);
    reset();
    onOpenChange(false);
  };

  return (
    <AppDialog
      open={open}
      onOpenChange={v => { if (!v) reset(); onOpenChange(v); }}
      title={`Send notification: ${user.username}`}
      description={`Email will be sent to ${user.email || '(no email configured)'}`}
      size="md"
      confirmLabel={notify.isPending ? 'Sending…' : 'Send notification'}
      confirmDisabled={!canSubmit || !user.email}
      isBusy={notify.isPending}
      lockOnBusy
      onConfirm={onConfirm}
    >
      <label className="block text-sm text-neutral-300">
        Subject *
        <input autoFocus value={title} onChange={e => setTitle(e.target.value)}
          placeholder="e.g. Scheduled maintenance this weekend"
          className="mt-1 w-full bg-black border border-neutral-700 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-emerald-500" />
      </label>
      <label className="block text-sm text-neutral-300">
        Message *
        <textarea value={message} onChange={e => setMessage(e.target.value)} rows={4}
          placeholder="Write the notification body…"
          className="mt-1 w-full bg-black border border-neutral-700 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-emerald-500 resize-y" />
      </label>
      {notify.isError && (
        <p className="text-sm text-rose-400">{notify.error?.message ?? 'Could not send notification.'}</p>
      )}
    </AppDialog>
  );
}

/* ─── Action menu for each row ────────────────────────────── */

type DialogKind = 'edit' | 'password' | 'roles' | 'delete' | 'notify';
type CommsAction = 'welcome' | 'reset' | 'notify';
type EphemeralState = 'idle' | 'pending' | 'success' | 'error';

function ActionEphemeralHint({
  state,
  text,
}: {
  state: EphemeralState;
  text?: string;
}) {
  if (state === 'idle' || !text) return null;

  const tone =
    state === 'pending'
      ? 'border-amber-700/80 bg-amber-900/90 text-amber-200'
      : state === 'success'
        ? 'border-emerald-700/80 bg-emerald-900/90 text-emerald-200'
        : 'border-rose-700/80 bg-rose-900/90 text-rose-200';

  return (
    <span
      className={`pointer-events-none absolute -top-7 left-1/2 -translate-x-1/2 whitespace-nowrap rounded px-2 py-0.5 text-[10px] font-medium border shadow-lg ${tone}`}
    >
      {text}
    </span>
  );
}

function RowActions({
  user,
  onToggle,
  onDialog,
  onComms,
  commsVisual,
}: {
  user: IAMUser;
  onToggle: () => void;
  onDialog: (kind: DialogKind) => void;
  onComms: (action: CommsAction) => void;
  commsVisual: Record<CommsAction, { state: EphemeralState; text?: string }>;
}) {
  const deactivate = useDeactivateIAMUser();
  const activate   = useActivateIAMUser();
  const toggling   = deactivate.isPending || activate.isPending;

  const handleToggleStatus = () => {
    if (user.status === 'active') deactivate.mutate(user.id_user);
    else activate.mutate(user.id_user);
  };

  const hasEmail = !!user.email;

  return (
    <div className="flex items-center gap-1">
      <button onClick={() => onDialog('edit')} title="Edit profile"
        className="text-xs text-neutral-500 hover:text-neutral-200 px-1.5 py-1 rounded hover:border-neutral-600">
        ✎
      </button>
      <button onClick={() => onDialog('password')} title="Reset password"
        className="text-xs text-neutral-500 hover:text-amber-300 px-1.5 py-1 rounded hover:border-neutral-600">
        🔑
      </button>
      <button onClick={() => onDialog('roles')} title="Manage roles"
        className="text-xs text-neutral-500 hover:text-sky-300 px-1.5 py-1 rounded hover:border-neutral-600">
        👤
      </button>

      {/* ── Comms actions ── */}
      <span className="w-px h-4 bg-neutral-700 mx-0.5" />
      <button onClick={() => onComms('welcome')} disabled={!hasEmail}
        title={hasEmail ? 'Send welcome email' : 'No email configured'}
        className={`relative text-xs px-1.5 py-1 rounded hover:border-neutral-600 ${
          hasEmail ? 'text-neutral-500 hover:text-emerald-400' : 'text-neutral-700 cursor-not-allowed'
        }`}>
        <ActionEphemeralHint state={commsVisual.welcome.state} text={commsVisual.welcome.text} />
        📧
      </button>
      <button onClick={() => onComms('reset')} disabled={!hasEmail}
        title={hasEmail ? 'Send password reset email' : 'No email configured'}
        className={`relative text-xs px-1.5 py-1 rounded hover:border-neutral-600 ${
          hasEmail ? 'text-neutral-500 hover:text-amber-400' : 'text-neutral-700 cursor-not-allowed'
        }`}>
        <ActionEphemeralHint state={commsVisual.reset.state} text={commsVisual.reset.text} />
        🔗
      </button>
      <button onClick={() => onComms('notify')} disabled={!hasEmail}
        title={hasEmail ? 'Send notification' : 'No email configured'}
        className={`relative text-xs px-1.5 py-1 rounded hover:border-neutral-600 ${
          hasEmail ? 'text-neutral-500 hover:text-violet-400' : 'text-neutral-700 cursor-not-allowed'
        }`}>
        <ActionEphemeralHint state={commsVisual.notify.state} text={commsVisual.notify.text} />
        📢
      </button>
      <span className="w-px h-4 bg-neutral-700 mx-0.5" />

      <button onClick={handleToggleStatus} disabled={toggling} title={user.status === 'active' ? 'Deactivate' : 'Activate'}
        className={`text-xs px-1.5 py-1 rounded hover:border-neutral-600 ${
          user.status === 'active'
            ? 'text-neutral-500 hover:text-rose-400'
            : 'text-neutral-500 hover:text-green-400'
        } ${toggling ? 'opacity-50 cursor-wait' : ''}`}>
        {user.status === 'active' ? '⏸' : '▶'}
      </button>
      <button onClick={() => onDialog('delete')} title="Delete user"
        className="text-xs text-neutral-500 hover:text-rose-400 px-1.5 py-1 rounded hover:border-neutral-600">
        ✕
      </button>
      <button onClick={onToggle} title="Toggle details"
        className="text-xs text-neutral-500 hover:text-neutral-300 px-2 py-1 rounded hover:border-neutral-600 ml-1">
        ▼
      </button>
    </div>
  );
}

/* ─── Main component ──────────────────────────────────────── */

export default function UserManagementV2() {
  useDocumentTitle('Admin', 'IAM', 'Users');

  const [search, setSearch]       = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [expandedId, setExpandedId]     = useState<number | null>(null);
  const [showCreate, setShowCreate]     = useState(false);

  // Dialog state
  const [dialogUser, setDialogUser]   = useState<IAMUser | null>(null);
  const [dialogKind, setDialogKind]   = useState<DialogKind | null>(null);
  const [commsVisualByKey, setCommsVisualByKey] = useState<Record<string, { state: EphemeralState; text?: string }>>({});
  const commsVisualTimers = useRef<Record<string, number>>({});

  // Inline toast state for comms feedback
  const [toast, setToast] = useState<{ kind: 'success' | 'error'; message: string } | null>(null);
  const toastTimerRef = useRef<number | null>(null);
  const showToast = useCallback((kind: 'success' | 'error', message: string) => {
    if (toastTimerRef.current) window.clearTimeout(toastTimerRef.current);
    setToast({ kind, message });
    toastTimerRef.current = window.setTimeout(() => setToast(null), 3500);
  }, []);
  useEffect(() => () => { if (toastTimerRef.current) window.clearTimeout(toastTimerRef.current); }, []);

  const setCommsVisual = useCallback((idUser: number, action: CommsAction, state: EphemeralState, text?: string, ttlMs = 2200) => {
    const key = `${idUser}:${action}`;
    const prev = commsVisualTimers.current[key];
    if (prev) window.clearTimeout(prev);

    setCommsVisualByKey(prevState => ({ ...prevState, [key]: { state, text } }));

    if (state !== 'pending') {
      commsVisualTimers.current[key] = window.setTimeout(() => {
        setCommsVisualByKey(prevState => {
          const next = { ...prevState };
          delete next[key];
          return next;
        });
        delete commsVisualTimers.current[key];
      }, ttlMs);
    }
  }, []);

  useEffect(() => () => {
    Object.values(commsVisualTimers.current).forEach((t) => window.clearTimeout(t));
    commsVisualTimers.current = {};
  }, []);

  // Comms mutations (fire-and-forget with toast)
  const sendWelcome = useSendWelcome();
  const sendReset   = useSendReset();

  const openDialog = useCallback((user: IAMUser, kind: DialogKind) => {
    setDialogUser(user);
    setDialogKind(kind);
  }, []);

  const closeDialog = useCallback(() => {
    setDialogUser(null);
    setDialogKind(null);
  }, []);

  const handleComms = useCallback((user: IAMUser, action: CommsAction) => {
    if (!user.email) {
      showToast('error', `User ${user.username} has no email address.`);
      return;
    }
    setCommsVisual(user.id_user, action, 'pending', 'Sending...');
    switch (action) {
      case 'welcome':
        sendWelcome.mutate(user.id_user, {
          onSuccess: (res: any) => {
            if (res?.pending_delivery) {
              setCommsVisual(user.id_user, action, 'success', 'Accepted');
              showToast('success', `Welcome email accepted for ${user.email} (provider is slow; delivery pending).`);
            } else {
              setCommsVisual(user.id_user, action, 'success', 'Sent');
              showToast('success', `Welcome email sent to ${user.email}`);
            }
          },
          onError: (e) => {
            setCommsVisual(user.id_user, action, 'error', 'Failed');
            showToast('error', e.message || 'Failed to send welcome email');
          },
        });
        break;
      case 'reset':
        sendReset.mutate(user.id_user, {
          onSuccess: (res: any) => {
            if (res?.pending_delivery) {
              setCommsVisual(user.id_user, action, 'success', 'Accepted');
              showToast('success', `Reset email accepted for ${user.email} (provider is slow; delivery pending).`);
            } else {
              setCommsVisual(user.id_user, action, 'success', 'Sent');
              showToast('success', `Password reset email sent to ${user.email}`);
            }
          },
          onError: (e) => {
            setCommsVisual(user.id_user, action, 'error', 'Failed');
            showToast('error', e.message || 'Failed to send reset email');
          },
        });
        break;
      case 'notify':
        setCommsVisual(user.id_user, action, 'idle');
        openDialog(user, 'notify');
        break;
    }
  }, [sendWelcome, sendReset, openDialog, showToast, setCommsVisual]);

  // Fetch users
  const { data, isLoading, isError, error } = useIAMUsers({
    search: search.trim() || undefined,
    status: statusFilter || undefined,
    limit: 100,
  });

  const users = data?.items ?? [];
  const total = data?.total ?? 0;

  const columns: ColumnDef<IAMUser>[] = useMemo(() => [
    {
      header: 'ID',
      accessor: 'id_user',
      Cell: (v: number) => (
        <span className="font-mono text-xs text-emerald-400">{v}</span>
      ),
    },
    { header: 'Username', accessor: 'username' },
    {
      header: 'Full Name',
      accessor: 'full_name',
      Cell: (v: string) => (
        <span className="text-neutral-300">{v || '—'}</span>
      ),
    },
    {
      header: 'Email',
      accessor: 'email',
      Cell: (v: string) => (
        v
          ? <span className="text-neutral-300 text-xs">{v}</span>
          : <span className="text-neutral-600 text-xs italic">no email</span>
      ),
    },
    {
      header: 'Status',
      accessor: 'status',
      Cell: (v: string) => (
        <span className={`text-xs px-2 py-0.5 rounded ${STATUS_CLS[v] ?? 'bg-neutral-700 text-neutral-300'}`}>
          {v}
        </span>
      ),
    },
    {
      header: 'Roles',
      accessor: 'roles' as any,
      Cell: (_v: any, row: IAMUser) => (
        <div className="flex gap-1 flex-wrap">
          {row.roles.map(r => (
            <span key={r} className="bg-sky-900/30 text-sky-400 text-xs px-1.5 py-0.5 rounded">
              {r}
            </span>
          ))}
        </div>
      ),
    },
    {
      header: '',
      accessor: '' as any,
      Cell: (_v: any, row: IAMUser) => {
        const visual = (action: CommsAction) => commsVisualByKey[`${row.id_user}:${action}`] || { state: 'idle' as EphemeralState };
        return (
        <RowActions
          user={row}
          onToggle={() => setExpandedId(prev => prev === row.id_user ? null : row.id_user)}
          onDialog={(kind) => openDialog(row, kind)}
          onComms={(action) => handleComms(row, action)}
          commsVisual={{
            welcome: visual('welcome'),
            reset: visual('reset'),
            notify: visual('notify'),
          }}
        />
        );
      },
    },
  ], [openDialog, handleComms, commsVisualByKey]);

  if (isLoading) {
    return <p className="text-neutral-400 p-4">Loading IAM users…</p>;
  }

  return (
    <section className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="space-y-1">
          <h2 className="text-xl font-semibold">IAM Users</h2>
          <p className="text-xs text-neutral-500">
            Hybrid architecture:&nbsp;
            <code className="text-emerald-500">workflow.iam_users</code> table +&nbsp;
            <code className="text-amber-400">iam.user</code> scope linkage.
            &nbsp;{total} user{total !== 1 ? 's' : ''} total.
          </p>
        </div>
        <button onClick={() => setShowCreate(true)}
          className="px-3 py-1.5 rounded bg-emerald-600 hover:bg-emerald-500 text-sm font-medium">
          + New user
        </button>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-3">
        <input
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder="Search by username or email…"
          className="bg-black border border-neutral-700 rounded px-3 py-2 text-sm w-full max-w-md focus:outline-none focus:ring-2 focus:ring-emerald-500"
        />
        <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)}
          className="bg-black border border-neutral-700 rounded px-3 py-2 text-sm">
          <option value="">All statuses</option>
          <option value="active">Active</option>
          <option value="inactive">Inactive</option>
          <option value="locked">Locked</option>
        </select>
      </div>

      {/* Error banner */}
      {isError && (
        <div className="bg-rose-900/20 border border-rose-800 rounded p-3 text-sm text-rose-300">
          Failed to load users: {error?.message ?? 'Unknown error'}
        </div>
      )}

      {/* Table */}
      <MinimalisticTable
        columns={columns}
        data={users}
        noDataMessage="No IAM users found."
      />

      {/* Expanded user detail */}
      {expandedId && users.find(u => u.id_user === expandedId) && (
        <UserDetailPanel user={users.find(u => u.id_user === expandedId)!} />
      )}

      {/* Dialogs */}
      <CreateUserDialog open={showCreate} onOpenChange={setShowCreate} />

      {dialogUser && dialogKind === 'edit' && (
        <EditProfileDialog user={dialogUser} open onOpenChange={v => { if (!v) closeDialog(); }} />
      )}
      {dialogUser && dialogKind === 'password' && (
        <ChangePasswordDialog user={dialogUser} open onOpenChange={v => { if (!v) closeDialog(); }} />
      )}
      {dialogUser && dialogKind === 'roles' && (
        <UpdateRolesDialog user={dialogUser} open onOpenChange={v => { if (!v) closeDialog(); }} />
      )}
      {dialogUser && dialogKind === 'delete' && (
        <DeleteUserDialog user={dialogUser} open onOpenChange={v => { if (!v) closeDialog(); }} />
      )}
      {dialogUser && dialogKind === 'notify' && (
        <SendNotifyDialog
          user={dialogUser}
          open
          onOpenChange={v => { if (!v) closeDialog(); }}
          onSent={(msg) => showToast('success', msg)}
        />
      )}

      {/* Inline toast */}
      {toast && (
        <div className={`fixed bottom-6 right-6 z-50 px-4 py-3 rounded-lg shadow-lg text-sm font-medium transition-all animate-in slide-in-from-bottom-2 ${
          toast.kind === 'success'
            ? 'bg-emerald-900/90 text-emerald-300 border border-emerald-700'
            : 'bg-rose-900/90 text-rose-300 border border-rose-700'
        }`}>
          <div className="flex items-center gap-2">
            <span>{toast.kind === 'success' ? '✓' : '✕'}</span>
            <span>{toast.message}</span>
            <button onClick={() => setToast(null)} className="ml-2 text-xs opacity-60 hover:opacity-100">✕</button>
          </div>
        </div>
      )}
    </section>
  );
}
